import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { saveBillingDetails } from '../../api/quotation/quotation.js';
import i18n from '../../i18n.js';
import styles from './belgian-billing-details-modal.module.scss';

const BelgianBillingDetailsModal = ({ quotation, quotationToken, isVisible, onConfirm, onClose }) => {
    const { t } = useTranslation();

    const [formData, setFormData] = useState({
        contact_name: '',
        contact_email: '',
        contact_phone: '',
        company: '',
        organisation_type: 'company',
        is_vat_liable: true,
        vat_number: '',
        kbo_number: '',
        street: '',
        street_number: '',
        zipcode: '',
        city: '',
        country_id: 3,
        billing_email: '',
        invoice_cc_email: '',
        client_order_number: '',
        is_confirmed: false,
    });

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errors, setErrors] = useState({});
    const [hasSaved, setHasSaved] = useState(false);

    React.useEffect(() => {
        if (quotation) {
            const customer = quotation.customer || {};
            const primaryContact = quotation.primary_contact || quotation.contact || {};
            const billingContact = quotation.billing_contact || primaryContact;
            const billingAddress = quotation.billing_address || quotation.address || {};
            const address = quotation.address || {};

            setFormData({
                contact_name: `${primaryContact.first_name || ''} ${primaryContact.last_name || ''}`.trim(),
                contact_email: primaryContact.email || customer.email || '',
                contact_phone: primaryContact.phone_number || customer.contact_number || '',
                company: customer.company || '',
                organisation_type: customer.organisation_type || 'company',
                is_vat_liable: customer.is_vat_liable !== undefined ? Boolean(customer.is_vat_liable) : (customer.vat_number ? true : true),
                vat_number: customer.vat_number || '',
                kbo_number: customer.kbo_number || '',
                street: billingAddress.street || address.street || '',
                street_number: billingAddress.street_number || address.street_number || '',
                zipcode: billingAddress.zipcode || address.zipcode || '',
                city: billingAddress.city || address.city || '',
                country_id: billingAddress.country_id || address.country_id || customer.country_id || 3,
                billing_email: billingContact.email || primaryContact.email || customer.email || '',
                invoice_cc_email: customer.invoice_cc_email || '',
                client_order_number: quotation.client_order_number || '',
                is_confirmed: false,
            });
        }
    }, [quotation, isVisible]);

    if (!isVisible) return null;

    const handleChange = (field, value) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: null }));
        }
    };

    const validate = () => {
        const errs = {};
        if (!formData.company?.trim()) {
            errs.company = t('Legal company name is required');
        }
        if (formData.is_vat_liable && !formData.vat_number?.trim()) {
            errs.vat_number = t('VAT number is required when VAT liable');
        }
        if (!formData.street?.trim()) {
            errs.street = t('Street is required');
        }
        if (!formData.street_number?.trim()) {
            errs.street_number = t('Street number is required');
        }
        if (!formData.zipcode?.trim()) {
            errs.zipcode = t('Zipcode is required');
        }
        if (!formData.city?.trim()) {
            errs.city = t('City is required');
        }
        if (!formData.billing_email?.trim()) {
            errs.billing_email = t('Billing email address is required');
        }
        if (!formData.is_confirmed) {
            errs.is_confirmed = t('Please confirm that the information is correct');
        }
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSubmit = async (e) => {
        if (e) {
            e.preventDefault();
            e.stopPropagation();
        }
        if (!validate()) {
            toast.error(t('Please fill in all required fields and confirm accuracy.'));
            return;
        }

        setIsSubmitting(true);
        try {
            const res = await saveBillingDetails(quotationToken, {
                ...formData,
                is_confirmed: true,
            });
            setIsSubmitting(false);
            setHasSaved(true);
            toast.success(t('Billing details updated successfully!'));
            if (onConfirm) {
                onConfirm(res);
            }
        } catch (error) {
            setIsSubmitting(false);
            const errMsg = error?.response?.data?.message || error?.message || 'Error saving billing details';
            toast.error(i18n.t(errMsg));
            console.error('Error saving billing details:', error);
        }
    };

    const isFormValid =
        Boolean(formData.company?.trim()) &&
        Boolean(formData.street?.trim()) &&
        Boolean(formData.street_number?.trim()) &&
        Boolean(formData.zipcode?.trim()) &&
        Boolean(formData.city?.trim()) &&
        Boolean(formData.billing_email?.trim()) &&
        (!formData.is_vat_liable || Boolean(formData.vat_number?.trim())) &&
        formData.is_confirmed;

    const isEnglish = i18n.language?.toLowerCase().startsWith('en');
    const isFrench = i18n.language?.toLowerCase().startsWith('fr') || i18n.language === 'be_fr';
    const yesLabel = isEnglish ? 'Yes' : (isFrench ? 'Oui' : 'Ja');
    const noLabel = isEnglish ? 'No' : (isFrench ? 'Non' : 'Nee');

    return (
        <div className={styles.overlay}>
            <div className={styles.modalDialog}>
                <div className={styles.modalHeader}>
                    <div className={styles.headerTopRow}>
                        <h2>{t('Controleer uw gegevens')}</h2>
                        {onClose && (
                            <button
                                type="button"
                                className={styles.closeBtn}
                                onClick={onClose}
                                aria-label="Close"
                            >
                                &times;
                            </button>
                        )}
                    </div>
                    <p>
                        {t('Uw offerte is geaccepteerd. Controleer hieronder de gegevens die bij ons bekend zijn en vul ontbrekende informatie aan. Zo zorgen we ervoor dat uw bestelling en factuur correct kunnen worden verwerkt.')}
                    </p>
                </div>

                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSubmit(e);
                    }}
                    className={styles.formContent}
                >
                    {/* Card 1: Klantgegevens */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <h3>{t('1. Klantgegevens')}</h3>
                            <span className={styles.badgeKnown}>{t('Reeds bekend')}</span>
                        </div>
                        <p className={styles.cardSubtitle}>
                            {t('Deze gegevens zijn bij ons bekend op basis van uw aanvraag of offerte.')}
                        </p>

                        <div className={styles.rowTwoCol}>
                            <div className={styles.formGroup}>
                                <label>{t('Klanttype')}</label>
                                <div className={styles.readOnlyBox}>
                                    <span>{t('Organisatie / bedrijf')}</span>
                                    <span className={styles.tagNote}>{t('Vastgelegd bij aanvraag')}</span>
                                </div>
                                <span className={styles.helperText}>
                                    {t('Het klanttype bepaalt welke gegevens hieronder nodig zijn.')}
                                </span>
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="contact_name">
                                    {t('Contactpersoon')} <span className={styles.requiredStar}>*</span>
                                </label>
                                <input
                                    id="contact_name"
                                    type="text"
                                    value={formData.contact_name}
                                    onChange={(e) => handleChange('contact_name', e.target.value)}
                                    placeholder={t('Bijv. Jan de Vries')}
                                />
                            </div>
                        </div>

                        <div className={styles.rowTwoCol}>
                            <div className={styles.formGroup}>
                                <label htmlFor="contact_email">
                                    {t('E-mailadres')} <span className={styles.requiredStar}>*</span>
                                </label>
                                <input
                                    id="contact_email"
                                    type="email"
                                    value={formData.contact_email}
                                    onChange={(e) => handleChange('contact_email', e.target.value)}
                                    placeholder={t('jan@voorbeeld.be')}
                                />
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="contact_phone">{t('Telefoonnummer')}</label>
                                <input
                                    id="contact_phone"
                                    type="text"
                                    value={formData.contact_phone}
                                    onChange={(e) => handleChange('contact_phone', e.target.value)}
                                    placeholder={t('+32 2 123 45 67')}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Card 2: Bedrijfsgegevens */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <h3>{t('2. Bedrijfsgegevens')}</h3>
                        </div>
                        <p className={styles.cardSubtitle}>
                            {t('Controleer de bedrijfsgegevens die bij ons bekend zijn. U kunt deze gegevens aanpassen wanneer ze niet correct zijn.')}
                        </p>

                        <div className={styles.rowTwoCol}>
                            <div className={styles.formGroup}>
                                <label htmlFor="company">
                                    {t('Juridische bedrijfsnaam')} <span className={styles.requiredStar}>*</span>
                                </label>
                                <input
                                    id="company"
                                    type="text"
                                    value={formData.company}
                                    onChange={(e) => handleChange('company', e.target.value)}
                                    placeholder={t('Voorbeeld Bedrijf NV')}
                                    className={errors.company ? styles.inputError : ''}
                                />
                                {errors.company && <span className={styles.errorText}>{errors.company}</span>}
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="organisation_type">
                                    {t('Type organisatie')} <span className={styles.optionalTag}>{t('optioneel')}</span>
                                </label>
                                <select
                                    id="organisation_type"
                                    value={formData.organisation_type || 'company'}
                                    onChange={(e) => handleChange('organisation_type', e.target.value)}
                                >
                                    <option value="company">{t('Onderneming')}</option>
                                    <option value="private">{t('Particulier')}</option>
                                </select>
                            </div>
                        </div>

                        <div className={styles.rowTwoCol}>
                            <div className={styles.formGroup}>
                                <label>
                                    {t('Is uw organisatie btw-plichtig?')} <span className={styles.requiredStar}>*</span>
                                </label>
                                <div className={styles.radioGroup}>
                                    <label>
                                        <input
                                            type="radio"
                                            name="is_vat_liable"
                                            checked={formData.is_vat_liable === true}
                                            onChange={() => handleChange('is_vat_liable', true)}
                                        />
                                        <span>{yesLabel}</span>
                                    </label>
                                    <label>
                                        <input
                                            type="radio"
                                            name="is_vat_liable"
                                            checked={formData.is_vat_liable === false}
                                            onChange={() => handleChange('is_vat_liable', false)}
                                        />
                                        <span>{noLabel}</span>
                                    </label>
                                </div>
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="vat_number">
                                    {t('Btw-nummer')} {formData.is_vat_liable && <span className={styles.requiredStar}>*</span>}
                                </label>
                                <input
                                    id="vat_number"
                                    type="text"
                                    value={formData.vat_number}
                                    onChange={(e) => handleChange('vat_number', e.target.value)}
                                    placeholder={t('Bijv. BE0123456789')}
                                    className={errors.vat_number ? styles.inputError : ''}
                                />
                                <span className={styles.helperText}>
                                    {t('Verplicht wanneer uw organisatie btw-plichtig is.')}
                                </span>
                                {errors.vat_number && <span className={styles.errorText}>{errors.vat_number}</span>}
                            </div>
                        </div>

                        <div className={styles.rowFull}>
                            <div className={styles.formGroup}>
                                <label htmlFor="kbo_number">
                                    {t('Ondernemingsnummer (KBO/BCE)')} <span className={styles.optionalTag}>{t('optioneel')}</span>
                                </label>
                                <input
                                    id="kbo_number"
                                    type="text"
                                    value={formData.kbo_number}
                                    onChange={(e) => handleChange('kbo_number', e.target.value)}
                                    placeholder={t('Vul hier uw ondernemingsnummer in')}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Card 3: Factuuradres */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <h3>{t('3. Factuuradres')}</h3>
                        </div>
                        <p className={styles.cardSubtitle}>
                            {t('Controleer het adres dat op uw factuur wordt vermeld.')}
                        </p>

                        <div className={styles.rowAddress}>
                            <div className={styles.formGroup}>
                                <label htmlFor="street">
                                    {t('Straat')} <span className={styles.requiredStar}>*</span>
                                </label>
                                <input
                                    id="street"
                                    type="text"
                                    value={formData.street}
                                    onChange={(e) => handleChange('street', e.target.value)}
                                    placeholder={t('Voorbeeldstraat')}
                                    className={errors.street ? styles.inputError : ''}
                                />
                                {errors.street && <span className={styles.errorText}>{errors.street}</span>}
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="street_number">
                                    {t('Huisnummer')} <span className={styles.requiredStar}>*</span>
                                </label>
                                <input
                                    id="street_number"
                                    type="text"
                                    value={formData.street_number}
                                    onChange={(e) => handleChange('street_number', e.target.value)}
                                    placeholder={t('10')}
                                    className={errors.street_number ? styles.inputError : ''}
                                />
                                {errors.street_number && <span className={styles.errorText}>{errors.street_number}</span>}
                            </div>
                        </div>

                        <div className={styles.rowAddressBottom}>
                            <div className={styles.formGroup}>
                                <label htmlFor="zipcode">
                                    {t('Postcode')} <span className={styles.requiredStar}>*</span>
                                </label>
                                <input
                                    id="zipcode"
                                    type="text"
                                    value={formData.zipcode}
                                    onChange={(e) => handleChange('zipcode', e.target.value)}
                                    placeholder={t('1000')}
                                    className={errors.zipcode ? styles.inputError : ''}
                                />
                                {errors.zipcode && <span className={styles.errorText}>{errors.zipcode}</span>}
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="city">
                                    {t('Plaats')} <span className={styles.requiredStar}>*</span>
                                </label>
                                <input
                                    id="city"
                                    type="text"
                                    value={formData.city}
                                    onChange={(e) => handleChange('city', e.target.value)}
                                    placeholder={t('Brussel')}
                                    className={errors.city ? styles.inputError : ''}
                                />
                                {errors.city && <span className={styles.errorText}>{errors.city}</span>}
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="country">
                                    {t('Land')} <span className={styles.requiredStar}>*</span>
                                </label>
                                <input
                                    id="country"
                                    type="text"
                                    value={quotation?.billing_address?.country?.name || quotation?.address?.country?.name || quotation?.customer?.country?.name || t('België')}
                                    disabled
                                />
                            </div>
                        </div>
                    </div>

                    {/* Card 4: Facturatiegegevens */}
                    <div className={styles.card}>
                        <div className={styles.cardHeader}>
                            <h3>{t('4. Facturatiegegevens')}</h3>
                        </div>
                        <p className={styles.cardSubtitle}>
                            {t('Controleer waar factuurcommunicatie naartoe moet worden gestuurd.')}
                        </p>

                        <div className={styles.rowTwoCol}>
                            <div className={styles.formGroup}>
                                <label htmlFor="billing_email">
                                    {t('Facturatie-e-mailadres')} <span className={styles.requiredStar}>*</span>
                                </label>
                                <input
                                    id="billing_email"
                                    type="email"
                                    value={formData.billing_email}
                                    onChange={(e) => handleChange('billing_email', e.target.value)}
                                    placeholder={t('finance@voorbeeld.be')}
                                    className={errors.billing_email ? styles.inputError : ''}
                                />
                                {errors.billing_email && <span className={styles.errorText}>{errors.billing_email}</span>}
                            </div>

                            <div className={styles.formGroup}>
                                <label htmlFor="invoice_cc_email">
                                    {t('Extra e-mailadres (CC)')} <span className={styles.optionalTag}>{t('optioneel')}</span>
                                </label>
                                <input
                                    id="invoice_cc_email"
                                    type="email"
                                    value={formData.invoice_cc_email}
                                    onChange={(e) => handleChange('invoice_cc_email', e.target.value)}
                                    placeholder={t('Vul eventueel een extra e-mailadres in')}
                                />
                            </div>
                        </div>

                        <div className={styles.rowFull}>
                            <div className={styles.formGroup}>
                                <label htmlFor="client_order_number">
                                    {t('Vereist uw organisatie een bestelnummer (PO-nummer) op de factuur?')}
                                </label>
                                <input
                                    id="client_order_number"
                                    type="text"
                                    value={formData.client_order_number}
                                    onChange={(e) => handleChange('client_order_number', e.target.value)}
                                    placeholder={t('Vul hier uw PO-nummer in')}
                                />
                                <span className={styles.helperText}>
                                    {t('Laat dit veld leeg wanneer een PO-nummer niet van toepassing is.')}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Confirmation Checkbox */}
                    <div className={styles.confirmationCard}>
                        <label className={styles.checkboxLabel}>
                            <input
                                type="checkbox"
                                checked={formData.is_confirmed}
                                onChange={(e) => handleChange('is_confirmed', e.target.checked)}
                            />
                            <span>{t('Ik bevestig dat bovenstaande gegevens correct en volledig zijn.')}</span>
                        </label>
                        <p className={styles.confirmNote}>
                            {t('We gebruiken deze gegevens voor het correct verwerken van uw bestelling en factuur en om, waar van toepassing, te voldoen aan de geldende facturatie- en e-facturatieregels.')}
                        </p>
                    </div>

                    {/* Submit Button */}
                    <div className={styles.modalFooter}>
                        {hasSaved && onConfirm && (
                            <button
                                type="button"
                                style={{
                                    marginRight: '1rem',
                                    background: '#ffffff',
                                    color: '#23b27c',
                                    border: '1px solid #23b27c',
                                    padding: '0.65rem 1.5rem',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontWeight: '500',
                                    fontSize: '0.95rem'
                                }}
                                onClick={onConfirm}
                            >
                                {t('Doorgaan naar bestelling')} &rarr;
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={handleSubmit}
                            disabled={!isFormValid || isSubmitting}
                        >
                            {isSubmitting ? t('Bezig met verwerken...') : t('Gegevens bevestigen en doorgaan')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

BelgianBillingDetailsModal.propTypes = {
    quotation: PropTypes.object,
    quotationToken: PropTypes.string.isRequired,
    isVisible: PropTypes.bool.isRequired,
    onConfirm: PropTypes.func,
    onClose: PropTypes.func,
};

export default BelgianBillingDetailsModal;
